import { type AppResponse } from '@autone/utils';
import { unset } from 'lodash-es';
import { type ArrayElement, type OmitDeep, type ValueOf } from 'type-fest';

import { type CoreFiltersFrontendStateShape } from '../core-state.types';

import {
  type CoreFilterPaths,
  type CreateTransformationConfig,
  type DeriveNonFilterableDimensionAttributeMap,
  type EndpointPathToFilterableDimensionAttributeMap,
  type FrontendFilterOption,
  type FrontendFilterOptionId,
  type GetPathResponseItem,
  type ParseFrontendFilterOptionFn,
  type RequestTransformers,
  type ResponseTransformers,
  type TransformCoreFilterRequestBody,
  type TransformerFactory,
  type UnsetNonFilterableDimensionAttributeFn,
} from './create-transformers.types';

/**
 * This factory function demands 3 arguments:
 *
 * 1. A mapping of endpoints to filterable `dimension.attributes`
 * 2. An exhaustive mapping of all remaining endpoints to non-filterable `dimension.attributes`
 * 3. A configuration object of callbacks deriving unique identifiers for each filter item in the
 * response based on the expected shape of each `dimension.attribute` in the request.
 *
 * Overall, this function will enforce that all `CoreFilterPaths` are handled by either the filterable
 * or non-filterable maps. We only ask for a `string` identifier for non-filterable `dimension.attributes`
 * for the purpose of supplying a top-level `id` property for the a `FrontendFilterOption` wrapper.
 *
 * The factory function will automatically generate the following properties:
 * - `requestTransformers`: An object of all endpoint-specific request transformers
 * - `responseTransformers`: An object of all endpoint-specific response transformers
 * - `__mappings`: An object containing the filterable and non-filterable maps
 * - `unsetNonFilterableDimensionAttributes`: A utility function to unset non-filterable dimension attributes from the state
 *
 * To generate the state shape for the given configuration, you can use the
 * `InferStateFromTransformerFactory` generic like this:
 *
 * ```ts
 * import { type InferStateFromTransformerFactory } from './create-transformers.types';
 *
 * const transformers = createTransformers(
 *   filterableMap,
 *   nonFilterableMap,
 *   config,
 * );
 *
 * type State = InferStateFromTransformerFactory<typeof transformers>;
 * ```
 *
 * `State` will contain all of your `dimension.attributes` mapped to their corresponding `FrontendFilterOption`
 * objects. We need to store the entire `FrontendFilterOption` as FE state (as opposed to just the `id`)
 * because components will always need access to the entire `payload` of selected items to render.
 */
export const createTransformers: TransformerFactory = <
  FilterableMap extends EndpointPathToFilterableDimensionAttributeMap,
  NonFilterableMap extends
    DeriveNonFilterableDimensionAttributeMap<FilterableMap>,
>({
  filterableMap,
  nonFilterableMap,
  config,
}: {
  filterableMap: FilterableMap;
  nonFilterableMap: NonFilterableMap;
  config: CreateTransformationConfig<FilterableMap>;
}) => {
  const allEndpoints = Object.keys({
    ...filterableMap,
    ...nonFilterableMap,
  }) as CoreFilterPaths[];

  const responseTransformersMap = {} as Record<
    CoreFilterPaths,
    ResponseTransformers[CoreFilterPaths]
  >;
  for (const endpoint of allEndpoints) {
    responseTransformersMap[endpoint] = ((
      response: AppResponse<'core', 'post', typeof endpoint, 200>,
    ) => {
      const transformedItems = response.items.map((item) =>
        buildFrontendFilterOption(endpoint, item),
      );
      return {
        page_info: response.page_info,
        items: transformedItems,
      };
    }) as ResponseTransformers[typeof endpoint];
  }

  const requestTransformersMap = {} as Record<
    CoreFilterPaths,
    ValueOf<RequestTransformers>
  >;
  for (const endpoint of allEndpoints) {
    requestTransformersMap[endpoint] = (({
      filters,
      ...rest
    }: TransformCoreFilterRequestBody<typeof endpoint>) => {
      const deserialisedRequest: Record<string, Record<string, unknown>> = {};
      for (const [dimension, attributes] of Object.entries(filters)) {
        if (attributes === undefined || attributes === null) continue;
        deserialisedRequest[dimension] = {};
        for (const [attribute, value] of Object.entries(attributes)) {
          if (typeof value === 'string') {
            deserialisedRequest[dimension][attribute] = value;
          } else if (Array.isArray(value)) {
            deserialisedRequest[dimension][attribute] = value.map((item) =>
              JSON.parse(item),
            );
          }
        }
      }
      return {
        filters: deserialisedRequest,
        ...rest,
      };
    }) as RequestTransformers[typeof endpoint];
  }

  type NonFilterableDimensionAttributes = ArrayElement<
    ValueOf<NonFilterableMap>
  >;
  const nonFilterableDimensionAttributes = Object.values(
    nonFilterableMap,
  ).flat() as NonFilterableDimensionAttributes[];

  /**
   * A utility to automatically unset non-filterable dimension attributes from the state.
   */
  const unsetNonFilterableDimensionAttributes: UnsetNonFilterableDimensionAttributeFn<
    NonFilterableMap
  > = <S extends CoreFiltersFrontendStateShape>(state: S) => {
    for (const attribute of nonFilterableDimensionAttributes) {
      unset(state, attribute);
    }
    return state as OmitDeep<S, NonFilterableDimensionAttributes>;
  };

  /**
   * A type-safe wrapper around `JSON.parse` of a `FrontendFilterOption` `id`, leveraging its `endpoint` tag
   * to safely determine the resulting type of the deserialised `id`.
   */
  const parseFrontendFilterOption: ParseFrontendFilterOptionFn<
    FilterableMap,
    NonFilterableMap
  > = (item) => {
    return JSON.parse(item.id);
  };

  /**
   * Build a single `FrontendFilterOption` directly from an endpoint + item,
   * mirroring what `responseTransformers[endpoint]` does for each item in a
   * paginated response.
   */
  const buildFrontendFilterOption = <E extends CoreFilterPaths>(
    endpoint: E,
    item: GetPathResponseItem<E>,
  ): FrontendFilterOption<E, GetPathResponseItem<E>> => {
    const deriveResponseIdentifier = config[endpoint];

    return {
      endpoint,
      id: JSON.stringify(
        deriveResponseIdentifier(item),
      ) as FrontendFilterOptionId,
      payload: item,
    };
  };

  return {
    requestTransformers: requestTransformersMap as RequestTransformers,
    responseTransformers: responseTransformersMap as ResponseTransformers,
    unsetNonFilterableDimensionAttributes,
    parseFrontendFilterOption,
    buildFrontendFilterOption,
    __mappings: {
      filterable: filterableMap,
      nonFilterable: nonFilterableMap,
    },
  };
};
